import { Component, EventEmitter, Input, Output, signal, computed, TemplateRef, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { ViewContainerRef } from '@angular/core';

export interface ImageItem {
    file: File;
    preview: string; // dataURL
    name: string;
    size: number; // bytes
    type: string;
}

@Component({
    selector: 'cf-image-uploader',
    standalone: true,
    imports: [CommonModule],
    templateUrl: './image-uploader.component.html',
    styleUrls: ['./image-uploader.component.scss']
})
export class ImageUploaderComponent {
    @ViewChild('viewerTpl') viewerTpl?: TemplateRef<any>;

    /** Imágenes iniciales (solo lectura). Acepta File[] o objetos con base64 */
    @Input() set initial(value: (File | { base64: string; nombre?: string; name?: string } | ImageItem)[] | null) {
        if (!value) return;
        const signature = this.buildInitialSignature(value);
        if (signature === this.lastInitialSignature) return;
        this.lastInitialSignature = signature;

        // Reset antes de rehidratar para evitar duplicados
        this.images.set([]);

        for (const v of value) {
            if (v instanceof File) {
                this.addFile(v, undefined, true);
            } else if (v && (v as any).base64) {
                const b64 = (v as any).base64;
                const name = (v as any).nombre || (v as any).name || 'imagen';
                // Solo procesar si es un data URL real (base64). Las URLs HTTP o de assets
                // no pueden convertirse a File y lanzarían una excepción en atob().
                if (!b64.startsWith('data:')) continue;
                try {
                    const f = this.dataURLtoFile(b64, name + '.png');
                    this.addFile(f, b64, true);
                } catch {
                    // data URL malformado, ignorar silenciosamente
                }
            } else if (v && (v as any).file instanceof File) {
                // Permitir rehidratar desde ImageItem
                const item = v as ImageItem;
                this.addFile(item.file, item.preview, true);
            }
        }
    }

    /** Límite de archivos */
    @Input() maxFiles = 6;
    /** Tamaño máximo por archivo (MB) */
    @Input() maxSizeMB = 5;
    /** Aceptar solo imágenes => se mantiene fijo */
    @Input() disabled = false;
    /** Cadena accept de input[type=file] para restringir tipos. Ej: 'image/png,image/svg+xml' o '.png,.svg' */
    @Input() accept: string = 'image/*';
    /** Permite subir archivos no imagen (por ejemplo PDF, DOC). Si es false solo se aceptan imágenes. */
    @Input() allowAnyFile = false;
    /** Texto del encabezado */
    @Input() label = 'Imágenes';
    /** Mensaje auxiliar */
    @Input() hint = 'Formatos permitidos: JPG, PNG, WEBP. Arrastre o haga click.';
    /** Si está en true, fuerza conversión a WEBP antes de emitir/guardar */
    @Input() forceWebp = false;
    /** Emite el array completo de imágenes vigentes */
    @Output() imagesChange = new EventEmitter<ImageItem[]>();

    images = signal<ImageItem[]>([]);
    dragOver = signal(false);
    errorMsg = signal<string | null>(null);
    viewerOpen = signal(false);
    viewerIndex = signal<number>(0);
    private overlayRef: OverlayRef | null = null;

    // Zoom & pan state for viewer
    zoom = signal(1);
    offsetX = signal(0);
    offsetY = signal(0);
    isPanning = signal(false);
    slideDir = signal<'left' | 'right' | null>(null);
    animFlip = signal(false);
    private lastClientX = 0;
    private lastClientY = 0;
    private lastTouchDistance: number | null = null;

    remaining = computed(() => this.maxFiles - this.images().length);

    private lastInitialSignature = '';

    private readonly placeholderPreview = 'data:image/gif;base64,R0lGODlhAQABAAAAACw='; // pixel transparente

    private emit() {
        this.imagesChange.emit(this.images());
    }

    private addFile(file: File, forcedPreview?: string, firstCharge?: boolean) {
        const isImg = this.isImage(file);
        if (!isImg && !this.allowAnyFile) {
            this.errorMsg.set(`El archivo ${file.name} no es una imagen soportada.`);
            return;
        }

        const process = async () => {
            let workingFile = file;
            let preview = forcedPreview;

            if (this.forceWebp && this.canConvertToWebp(workingFile)) {
                const converted = await this.convertToWebp(workingFile);
                if (converted) {
                    workingFile = converted.file;
                    preview = converted.preview;
                }
            }

            if (workingFile.size > this.maxSizeMB * 1024 * 1024) {
                this.errorMsg.set(`El archivo ${workingFile.name} excede el máximo de ${this.maxSizeMB}MB`);
                return;
            }
            if (this.images().length >= this.maxFiles) {
                this.errorMsg.set(`Máximo de ${this.maxFiles} imágenes alcanzado.`);
                return;
            }
            // Evitar duplicados por nombre+tamaño
            const alreadyExists = this.images().some((img) => img.name === workingFile.name && img.size === workingFile.size);
            if (alreadyExists) return;

            const reader = new FileReader();
            const pushItem = (computedPreview: string) => {
                const item: ImageItem = {
                    file: workingFile,
                    preview: computedPreview,
                    name: workingFile.name,
                    size: workingFile.size,
                    type: workingFile.type
                };
                this.images.update((arr) => [...arr, item]);
                firstCharge ? '' : this.emit();
            };

            reader.onload = () => pushItem((reader.result as string) || this.placeholderPreview);

            if (preview) {
                pushItem(preview);
            } else if (isImg) {
                reader.readAsDataURL(workingFile);
            } else {
                // Archivos no imagen: usar placeholder
                pushItem(this.placeholderPreview);
            }
        };

        process().catch(() => this.errorMsg.set(`No se pudo procesar ${file.name}. Intentalo nuevamente.`));
    }

    private canConvertToWebp(file: File): boolean {
        if (!file?.type?.startsWith('image/') || this.allowAnyFile) return false;
        // Evitar SVG/GIF que pueden perder animación
        if (/svg|gif/i.test(file.type) || /\.svg$|\.gif$/i.test(file.name || '')) return false;
        return true;
    }

    private async convertToWebp(file: File): Promise<{ file: File; preview: string } | null> {
        const dataUrl = await this.readAsDataURL(file).catch(() => null);
        if (!dataUrl) return null;

        const img = await this.loadImage(dataUrl).catch(() => null);
        if (!img) return null;

        const canvas = document.createElement('canvas');
        canvas.width = img.naturalWidth || img.width;
        canvas.height = img.naturalHeight || img.height;
        const ctx = canvas.getContext('2d');
        if (!ctx) return null;
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

        const webpDataUrl = canvas.toDataURL('image/webp', 0.82);
        const newName = (file.name || 'imagen').replace(/\.[^.]+$/, '') + '.webp';
        const webpFile = this.dataURLtoFile(webpDataUrl, newName);
        return { file: webpFile, preview: webpDataUrl };
    }

    private readAsDataURL(file: File): Promise<string> {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result as string);
            reader.onerror = () => reject(reader.error);
            reader.readAsDataURL(file);
        });
    }

    private loadImage(dataUrl: string): Promise<HTMLImageElement> {
        return new Promise((resolve, reject) => {
            const img = new Image();
            img.onload = () => resolve(img);
            img.onerror = (err) => reject(err);
            img.src = dataUrl;
        });
    }

    private buildInitialSignature(value: (File | { base64: string; nombre?: string; name?: string } | ImageItem)[]): string {
        try {
            const mapped = value.map((v) => {
                if (v instanceof File) {
                    return { n: v.name, s: v.size };
                }
                if ((v as any).file instanceof File) {
                    const item = v as ImageItem;
                    return { n: item.name, s: item.size };
                }
                const name = (v as any).nombre || (v as any).name || 'imagen';
                const b64 = (v as any).base64 || '';
                return { n: name, s: b64.length }; // usar longitud como huella
            });
            return JSON.stringify(mapped);
        } catch {
            return '';
        }
    }

    onFileInput(ev: Event) {
        const input = ev.target as HTMLInputElement;
        const files = input.files;
        if (!files) return;
        const arr = Array.from(files);
        const remain = this.remaining();
        if (arr.length > remain) {
            this.errorMsg.set(`Seleccionaste ${arr.length} archivo(s), y solo podés agregar ${remain} más.`);
            input.value = '';
            return;
        }
        arr.forEach((f) => this.addFile(f));
        input.value = '';
    }

    onDrop(ev: DragEvent) {
        ev.preventDefault();
        this.dragOver.set(false);
        if (this.disabled) return;
        if (ev.dataTransfer?.files?.length) {
            const arr = Array.from(ev.dataTransfer.files);
            const remain = this.remaining();
            if (arr.length > remain) {
                this.errorMsg.set(`Seleccionaste ${arr.length} archivo(s), y solo podés agregar ${remain} más.`);
                return;
            }
            arr.forEach((f) => this.addFile(f));
        }
    }

    onDragOver(ev: DragEvent) {
        ev.preventDefault();
        if (!this.disabled) this.dragOver.set(true);
    }
    onDragLeave(ev: DragEvent) {
        ev.preventDefault();
        this.dragOver.set(false);
    }

    remove(i: number) {
        this.images.update((arr) => arr.filter((_, idx) => idx !== i));
        this.emit();
    }

    isTouch = typeof navigator !== 'undefined' && (navigator.maxTouchPoints || 0) > 0;

    constructor(
        private overlay: Overlay,
        private vcr: ViewContainerRef
    ) {}

    open(i: number) {
        if (!this.images()[i]) return;
        this.viewerIndex.set(i);
        this.viewerOpen.set(true);

        // Reset zoom/pan on open
        this.zoom.set(1);
        this.offsetX.set(0);
        this.offsetY.set(0);
        this.isPanning.set(false);

        // If already open, do nothing
        if (this.overlayRef?.hasAttached()) return;

        const positionStrategy = this.overlay.position().global().centerHorizontally().centerVertically();
        this.overlayRef = this.overlay.create({
            hasBackdrop: false, // usamos máscara propia en el template
            panelClass: 'iu-viewer-panel',
            positionStrategy,
            disposeOnNavigation: true,
            scrollStrategy: this.overlay.scrollStrategies.block()
        });

        const tpl = this.viewerTpl;
        if (tpl) {
            const portal = new TemplatePortal(tpl, this.vcr);
            this.overlayRef.attach(portal);
        }

        this.overlayRef.keydownEvents().subscribe((ev: KeyboardEvent) => {
            if (ev.key === 'Escape') this.closeViewer();
            if (ev.key === 'ArrowRight') this.next();
            if (ev.key === 'ArrowLeft') this.prev();
        });
    }

    closeViewer() {
        this.viewerOpen.set(false);
        if (this.overlayRef) {
            this.overlayRef.detach();
            this.overlayRef.dispose();
            this.overlayRef = null;
        }
        // Reset zoom/pan on close
        this.zoom.set(1);
        this.offsetX.set(0);
        this.offsetY.set(0);
        this.isPanning.set(false);
    }

    // ---------- Viewer interactions (zoom/pan) ----------
    zoomTransform() {
        return `translate3d(${this.offsetX()}px, ${this.offsetY()}px, 0) scale(${this.zoom()})`;
    }

    onDblClick(ev: MouseEvent) {
        ev.preventDefault();
        if (this.zoom() > 1) {
            this.zoom.set(1);
            this.offsetX.set(0);
            this.offsetY.set(0);
        } else {
            this.zoom.set(2);
        }
    }

    onWheel(ev: WheelEvent) {
        ev.preventDefault();
        const current = this.zoom();
        const factor = Math.exp(-ev.deltaY * 0.0015); // smooth zoom
        const next = this.clampScale(current * factor);
        const ratio = next / current;
        // Scale offsets to give a sense of zooming toward center
        this.offsetX.update((v) => v * ratio);
        this.offsetY.update((v) => v * ratio);
        this.zoom.set(next);
    }

    onPanStart(ev: MouseEvent | Touch) {
        if (this.zoom() <= 1) return; // no pan when not zoomed
        this.isPanning.set(true);
        this.lastClientX = ev.clientX;
        this.lastClientY = ev.clientY;
    }

    onPanMove(ev: MouseEvent | Touch) {
        if (!this.isPanning()) return;
        const dx = ev.clientX - this.lastClientX;
        const dy = ev.clientY - this.lastClientY;
        this.offsetX.update((v) => v + dx);
        this.offsetY.update((v) => v + dy);
        this.lastClientX = ev.clientX;
        this.lastClientY = ev.clientY;
    }

    onPanEnd() {
        this.isPanning.set(false);
    }

    onTouchStart(ev: TouchEvent) {
        if (ev.touches.length === 2) {
            this.lastTouchDistance = this.touchDistance(ev.touches[0], ev.touches[1]);
        } else if (ev.touches.length === 1) {
            if (this.zoom() > 1) this.onPanStart(ev.touches[0]);
        }
    }

    onTouchMove(ev: TouchEvent) {
        if (ev.touches.length === 2 && this.lastTouchDistance != null) {
            ev.preventDefault();
            const d = this.touchDistance(ev.touches[0], ev.touches[1]);
            const factor = d / (this.lastTouchDistance || d);
            const next = this.clampScale(this.zoom() * factor);
            const ratio = next / this.zoom();
            this.offsetX.update((v) => v * ratio);
            this.offsetY.update((v) => v * ratio);
            this.zoom.set(next);
            this.lastTouchDistance = d;
        } else if (ev.touches.length === 1 && this.isPanning()) {
            this.onPanMove(ev.touches[0]);
        }
    }

    onTouchEnd() {
        this.isPanning.set(false);
        this.lastTouchDistance = null;
    }

    private touchDistance(a: Touch, b: Touch) {
        const dx = a.clientX - b.clientX;
        const dy = a.clientY - b.clientY;
        return Math.hypot(dx, dy);
    }
    private clampScale(s: number) {
        return Math.min(5, Math.max(1, s));
    }

    onViewerImageClick(ev: MouseEvent) {
        if (this.isTouch) return; // no click zoom on mobile/tablet
        ev.preventDefault();
        if (this.zoom() > 1) {
            this.zoom.set(1);
            this.offsetX.set(0);
            this.offsetY.set(0);
        } else {
            this.zoom.set(2);
        }
    }
    // Next/Prev with slide animation direction
    next() {
        const n = this.images().length;
        if (!n) return;
        this.slideDir.set('right');
        this.animFlip.set(!this.animFlip());
        this.viewerIndex.set((this.viewerIndex() + 1) % n);
    }
    prev() {
        const n = this.images().length;
        if (!n) return;
        this.slideDir.set('left');
        this.animFlip.set(!this.animFlip());
        this.viewerIndex.set((this.viewerIndex() - 1 + n) % n);
    }

    getSlideClass() {
        const dir = this.slideDir();
        const flip = this.animFlip();
        if (!dir) return '';
        if (dir === 'right') return flip ? 'iu-slide-right-b' : 'iu-slide-right-a';
        return flip ? 'iu-slide-left-b' : 'iu-slide-left-a';
    }

    bytesHuman(size: number) {
        if (size < 1024) return size + ' B';
        const kb = size / 1024;
        if (kb < 1024) return kb.toFixed(1) + ' KB';
        const mb = kb / 1024;
        return mb.toFixed(2) + ' MB';
    }

    isImage(f: File) {
        // Cuando accept es el default, permitir imágenes comunes
        const acc = (this.accept || '').trim();
        if (!acc || acc === 'image/*') {
            return /^image\//.test(f.type) || /\.(png|jpe?g|gif|webp|svg)$/i.test(f.name);
        }

        // Parsear la cadena accept en entradas separadas por coma
        const parts = acc.split(',').map((s) => s.trim()).filter(Boolean);
        // Coincidir por mimetype exacto (image/png, image/svg+xml) o por extensión (.png, .svg)
        const mime = (f.type || '').toLowerCase();
        const name = (f.name || '').toLowerCase();
        for (const p of parts) {
            if (!p) continue;
            const token = p.toLowerCase();
            if (token === 'image/*') {
                if (mime.startsWith('image/')) return true;
            } else if (token.startsWith('.')) {
                if (name.endsWith(token)) return true;
            } else if (/^[\w-]+\/[\w.+-]+$/.test(token)) {
                if (mime === token) return true;
            }
        }
        this.errorMsg.set(`El archivo ${f.name} no coincide con los tipos permitidos (${acc}).`);
        return false;
    }

    dataURLtoFile(dataUrl: string, filename: string) {
        const arr = dataUrl.split(',');
        const mimeMatch = arr[0].match(/:(.*?);/);
        const mime = mimeMatch ? mimeMatch[1] : 'image/png';
        const bstr = atob(arr[1]);
        let n = bstr.length;
        const u8arr = new Uint8Array(n);
        while (n--) u8arr[n] = bstr.charCodeAt(n);
        return new File([u8arr], filename, { type: mime });
    }
}
